import React, { useState, useRef, useEffect } from "react";

// Get environment configuration
const getApiConfig = () => {
  return {
    API_BASE: process.env.REACT_APP_API_BASE_URL,
    DOMAIN_URL: process.env.REACT_APP_DOMAIN_URL || window.location.origin,
    S3_BUCKET: process.env.REACT_APP_S3_BUCKET,
  };
};

const config = getApiConfig();

export default function S3FileUploader() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [processingJobs, setProcessingJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Set your password here
  const SITE_PASSWORD = "cranberries";

  // API endpoints (replace with your actual endpoints)
  const API_BASE = config.API_BASE;

  // Get the processor WebSocket URL
  const getWebSocketUrl = () => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws`;
  };

  // WebSocket for real-time updates
  // WebSocket for real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log("Setting up WebSocket connection...");

    // Load Socket.IO client
    const script = document.createElement("script");
    script.src = "https://cdn.socket.io/4.7.2/socket.io.min.js";
    script.onload = () => {
      // Connect to Socket.IO through CloudFront
      // Socket.IO will automatically use the /socket.io path
      const socket = window.io({
        path: "/socket.io/",
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socket.on("connect", () => {
        console.log("WebSocket connected successfully!");
        console.log("Socket ID:", socket.id);
      });

      socket.on("connect_error", (error) => {
        console.error("WebSocket connection error:", error);
      });

      socket.on("disconnect", (reason) => {
        console.log("WebSocket disconnected:", reason);
      });

      socket.on("processing_started", (data) => {
        console.log("Processing started:", data);
        // Always add as new job - no duplicates since we don't pre-add
        setProcessingJobs((prev) => [
          ...prev,
          {
            id: data.jobId,
            fileName: data.fileName,
            status: "processing",
            startTime: new Date(),
          },
        ]);
      });

      socket.on("processing_completed", (data) => {
        console.log("Processing completed:", data);
        // Remove from processing jobs
        setProcessingJobs((prev) =>
          prev.filter((job) => job.id !== data.jobId)
        );
        // Add to completed jobs
        setCompletedJobs((prev) => [
          ...prev,
          {
            id: data.jobId,
            fileName: data.fileName,
            downloadUrl: `/download/${data.jobId}`,
            completedTime: new Date(),
          },
        ]);
      });

      socket.on("processing_failed", (data) => {
        console.log("Processing failed:", data);
        // Remove from processing jobs
        setProcessingJobs((prev) =>
          prev.filter((job) => job.id !== data.jobId)
        );
        // You could add to a failed jobs list or show an alert
        //alert(`Processing failed for ${data.fileName}: ${data.error}`);
      });

      socket.on("processing_failed", (data) => {
        console.log("Processing failed:", data);
        setProcessingJobs((prev) =>
          prev.map((job) =>
            job.id === data.jobId
              ? { ...job, status: "failed", error: data.error }
              : job
          )
        );
      });

      // Store socket reference for cleanup
      window.socketConnection = socket;
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (window.socketConnection) {
        window.socketConnection.disconnect();
        window.socketConnection = null;
      }
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [isAuthenticated]);

  // Get pre-signed URL for upload
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === SITE_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError("");
    } else {
      setAuthError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setFiles([]);
    setUploadProgress({});
    setProcessingJobs([]);
    setCompletedJobs([]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };
  const createZipFromFiles = async (files, folderName) => {
    // Load JSZip library dynamically
    if (!window.JSZip) {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
      document.head.appendChild(script);
      await new Promise((resolve) => (script.onload = resolve));
    }

    const zip = new window.JSZip();

    // Add files to zip
    for (const file of files) {
      const relativePath = file.webkitRelativePath || file.name;
      const pathInZip = relativePath.replace(folderName + "/", "");
      zip.file(pathInZip, file);
    }

    // Generate zip file
    const zipBlob = await zip.generateAsync({ type: "blob" });
    return new File([zipBlob], `${folderName}.zip`, {
      type: "application/zip",
    });
  };
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length > 0) {
      // Check if this is a folder upload
      const isFolder =
        selectedFiles[0].webkitRelativePath !== undefined &&
        selectedFiles[0].webkitRelativePath !== "";

      if (isFolder) {
        // Get folder name from the first file's path
        const folderName = selectedFiles[0].webkitRelativePath.split("/")[0];

        // Check if folder contains required spectral image files
        const hasHdrFiles = selectedFiles.some((f) => f.name.endsWith(".hdr"));
        const hasDarkRef = selectedFiles.some((f) =>
          f.name.includes("DARKREF")
        );
        const hasWhiteRef = selectedFiles.some((f) =>
          f.name.includes("WHITEREF")
        );

        if (!hasHdrFiles) {
          alert(
            "No HDR files found in the selected folder. Please select a folder containing spectral image files."
          );
          return;
        }

        if (!hasDarkRef || !hasWhiteRef) {
          const proceed = window.confirm(
            "Warning: No DARKREF or WHITEREF files found. " +
              "Processing may fail without reference files. Continue anyway?"
          );
          if (!proceed) return;
        }

        // Create a zip file from the folder contents
        try {
          const zipFile = await createZipFromFiles(selectedFiles, folderName);
          addFiles([zipFile]);
        } catch (error) {
          console.error("Error creating zip:", error);
          alert("Failed to package folder for upload. Please try again.");
        }
      } else {
        // Regular file upload (archives)
        const validFiles = selectedFiles.filter(
          (file) =>
            file.name.endsWith(".zip") ||
            file.name.endsWith(".tar") ||
            file.name.endsWith(".tar.gz")
        );

        if (validFiles.length === 0) {
          alert(
            "Please select ZIP or TAR archives containing spectral image files."
          );
          return;
        }

        addFiles(validFiles);
      }
    }
  };

  const addFiles = (newFiles) => {
    const fileObjects = newFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: "pending",
    }));
    setFiles((prev) => [...prev, ...fileObjects]);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[id];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.startsWith("image/")) return "🖼️";
    if (type.startsWith("video/")) return "🎥";
    if (type.startsWith("audio/")) return "🎵";
    if (type.includes("pdf")) return "📄";
    if (type.includes("word")) return "📝";
    if (type.includes("excel") || type.includes("spreadsheet")) return "📊";
    if (type.includes("zip") || type.includes("rar")) return "📦";
    return "📁";
  };

  // Get pre-signed URL for upload
  const getPresignedUrl = async (fileName, fileType) => {
    const response = await fetch(`${config.API_BASE}/presigned-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName, fileType }),
    });
    const data = await response.json();
    return {
      uploadUrl: data.uploadUrl,
      fileKey: data.fileKey,
      jobId: data.jobId || Date.now(), // fallback jobId
    };
  };

  // Upload file to S3 using presigned URL
  const uploadToS3 = async (file, presignedUrl, fileObj) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress((prev) => ({
            ...prev,
            [fileObj.id]: Math.round(progress),
          }));
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error("Upload failed"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));

      xhr.open("PUT", presignedUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  };

  // Upload files - simplified without adding to processing queue
  const uploadFiles = async () => {
    if (files.length === 0) return;
    setIsUploading(true);
    const pendingFiles = files.filter((f) => f.status === "pending");

    for (const fileObj of pendingFiles) {
      try {
        // Update status to uploading
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, status: "uploading" } : f
          )
        );

        // 1. Get presigned URL
        const { uploadUrl, fileKey, jobId } = await getPresignedUrl(
          fileObj.name,
          fileObj.file.type
        );

        // 2. Upload to S3
        await uploadToS3(fileObj.file, uploadUrl, fileObj);

        // 3. Report progress 100%
        setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 100 }));

        // 4. Mark file as completed
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, status: "completed" } : f
          )
        );

        // Don't add to processing jobs here - let WebSocket handle it
        // when EC2 actually starts processing
      } catch (error) {
        console.error("Error uploading file:", error);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, status: "failed" } : f
          )
        );
      }
    }
    setIsUploading(false);
  };
  // Download processed file - direct browser download
  const downloadProcessedFile = async (jobId, fileName) => {
    try {
      // Get the download URL from your API
      console.log("Requesting download for job:", jobId);
      const response = await fetch(`${API_BASE}/download/${jobId}`);
      const data = await response.json();

      console.log("Download response:", data);
      if (!response.ok) {
        throw new Error(data.error || "Download failed");
      }

      // Direct navigation - browser handles download natively
      window.location.href = data.downloadUrl;

      // Optional: Remove from completed jobs after a delay
      setTimeout(() => {
        setCompletedJobs((prev) => prev.filter((job) => job.id !== jobId));
      }, 3000);
    } catch (error) {
      console.error("Download error:", error);
      alert(`Failed to download file: ${error.message}`);
    }
  };

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== "completed"));
    setUploadProgress({});
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-700 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Access Required
            </h1>
            <p className="text-gray-400">
              Enter the password to access the file uploader
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                placeholder="Enter password"
                required
              />
            </div>

            {authError && (
              <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              Access Uploader
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-gray-500">
            Secure file upload portal
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-between items-center mb-4">
            <div className="text-left">
              <div className="text-xs text-gray-400">
                API: {config.API_BASE}
              </div>
              <div className="text-xs text-gray-400">
                Env: {process.env.NODE_ENV || "development"}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition duration-200 text-sm"
            >
              🚪 Logout
            </button>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Spectral Image Processor
          </h1>
          <p className="text-gray-300 text-lg"></p>
        </div>
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 mb-6 transition-all duration-300 bg-gray-800/50 backdrop-blur-sm ${
            isDragging
              ? "border-blue-400 bg-blue-900/30 scale-105"
              : "border-gray-600 hover:border-gray-500"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <div className="text-6xl mb-4">{isDragging ? "📥" : ""}</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {isDragging ? "Drop your folder here" : ""}
            </h3>
            <p className="text-gray-400 mb-2">
              Upload folders containing HDR files with DARKREF and WHITEREF
              references
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Supported: Folders, ZIP, or TAR archives containing .hdr/.dat
              files
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => folderInputRef.current?.click()}
                className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2"
              >
                📂 Select Folder
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2"
              >
                📦 Select Archive (ZIP/TAR)
              </button>
            </div>

            <div className="mt-6 p-4 bg-gray-700/50 rounded-lg text-left max-w-md mx-auto">
              <h4 className="text-white font-semibold mb-2">
                📋 Required File Structure:
              </h4>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>
                  • Sample files: <code className="text-blue-400">*.hdr</code>{" "}
                </li>
                <li>
                  • Dark reference:{" "}
                  <code className="text-blue-400">DARKREF_*.hdr</code>
                </li>
                <li>
                  • White reference:{" "}
                  <code className="text-blue-400">WHITEREF_*.hdr</code>
                </li>
                <li>
                  • Each HDR file should have a corresponding .raw or .bil file
                </li>
              </ul>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".zip,.tar,.tar.gz"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory="true"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
        {/* Processing Status */}
        {processingJobs.length > 0 && (
          <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-700 p-6 mb-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              ⚙️ Processing Files ({processingJobs.length})
            </h3>
            <div className="space-y-3">
              {processingJobs.map((job) => (
                <div
                  key={job.id}
                  className={`flex items-center gap-4 p-3 rounded-lg border ${
                    job.status === "queued"
                      ? "bg-blue-900/30 border-blue-700"
                      : job.status === "processing"
                      ? "bg-yellow-900/30 border-yellow-700"
                      : "bg-red-900/30 border-red-700"
                  }`}
                >
                  <div className="text-2xl">
                    {job.status === "queued" ? "⏳" : "⚙️"}
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-medium ${
                        job.status === "queued"
                          ? "text-blue-200"
                          : job.status === "processing"
                          ? "text-yellow-200"
                          : "text-red-200"
                      }`}
                    >
                      {job.fileName}
                    </div>
                    <div
                      className={`text-sm ${
                        job.status === "queued"
                          ? "text-blue-300"
                          : job.status === "processing"
                          ? "text-yellow-300"
                          : "text-red-300"
                      }`}
                    >
                      {job.status === "queued"
                        ? "Waiting in queue..."
                        : job.status === "processing"
                        ? "Processing on EC2..."
                        : job.status === "failed"
                        ? `Failed: ${job.error}`
                        : job.status}
                    </div>
                  </div>
                  {job.status === "processing" && (
                    <div className="animate-spin text-yellow-400">🔄</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Completed Downloads */}
        {completedJobs.length > 0 && (
          <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-700 p-6 mb-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              📥 Ready for Download ({completedJobs.length})
            </h3>
            <div className="space-y-3">
              {completedJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center gap-4 p-3 bg-green-900/30 rounded-lg border border-green-700"
                >
                  <div className="text-2xl">✅</div>
                  <div className="flex-1">
                    <div className="font-medium text-green-200">
                      {job.fileName}
                    </div>
                    <div className="text-sm text-green-300">
                      Processed at {job.completedTime.toLocaleTimeString()}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadProcessedFile(job.id, job.fileName)}
                    className="bg-green-600 hover:bg-green-500 text-white font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center gap-2"
                  >
                    📥 Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* File List */}
        {files.length > 0 && (
          <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-700 p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">
                Files ({files.length})
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={clearCompleted}
                  className="text-gray-400 hover:text-gray-200 px-3 py-1 rounded text-sm"
                >
                  Clear Completed
                </button>
                <button
                  onClick={uploadFiles}
                  disabled={
                    isUploading || files.every((f) => f.status !== "pending")
                  }
                  className="bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  {isUploading ? "Uploading..." : "Upload All"}
                </button>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {files.map((fileObj) => (
                <div
                  key={fileObj.id}
                  className="flex items-center gap-4 p-3 bg-gray-700/50 rounded-lg"
                >
                  <div className="text-2xl">{getFileIcon(fileObj.type)}</div>

                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-200 truncate">
                      {fileObj.name}
                    </div>
                    <div className="text-sm text-gray-400">
                      {formatFileSize(fileObj.size)}
                    </div>

                    {fileObj.status === "uploading" && (
                      <div className="mt-2">
                        <div className="bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${uploadProgress[fileObj.id] || 0}%`,
                            }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {uploadProgress[fileObj.id] || 0}%
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {fileObj.status === "pending" && (
                      <span className="text-yellow-400 text-sm">
                        ⏳ Pending
                      </span>
                    )}
                    {fileObj.status === "uploading" && (
                      <span className="text-blue-400 text-sm">
                        📤 Uploading
                      </span>
                    )}
                    {fileObj.status === "completed" && (
                      <span className="text-green-400 text-sm">
                        ✅ Uploaded
                      </span>
                    )}
                    {fileObj.status === "failed" && (
                      <span className="text-red-400 text-sm">❌ Failed</span>
                    )}

                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="text-red-400 hover:text-red-300 p-1 rounded"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Stats */}
        {(files.length > 0 ||
          processingJobs.length > 0 ||
          completedJobs.length > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-800/70 backdrop-blur-sm rounded-lg shadow-md p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-yellow-400">
                {files.filter((f) => f.status === "pending").length}
              </div>
              <div className="text-gray-300 text-sm">Pending</div>
            </div>
            <div className="bg-gray-800/70 backdrop-blur-sm rounded-lg shadow-md p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-blue-400">
                {files.filter((f) => f.status === "uploading").length}
              </div>
              <div className="text-gray-300 text-sm">Uploading</div>
            </div>
            <div className="bg-gray-800/70 backdrop-blur-sm rounded-lg shadow-md p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-green-400">
                {files.filter((f) => f.status === "completed").length}
              </div>
              <div className="text-gray-300 text-sm">Uploaded</div>
            </div>
            <div className="bg-gray-800/70 backdrop-blur-sm rounded-lg shadow-md p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-orange-400">
                {processingJobs.length}
              </div>
              <div className="text-gray-300 text-sm">Processing</div>
            </div>
            <div className="bg-gray-800/70 backdrop-blur-sm rounded-lg shadow-md p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-purple-400">
                {completedJobs.length}
              </div>
              <div className="text-gray-300 text-sm">Ready</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
